import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { INTERNAL_APP_CONFIG } from '../../core/token';

@Injectable()
export class ThemeManager {
    private readonly _config: Record<string, string> = inject(INTERNAL_APP_CONFIG).ui.vars || {};
    private readonly _appConfig = inject(INTERNAL_APP_CONFIG);
    private readonly _document: Document = inject(DOCUMENT);

    startup() {
        // Set UI variables
        Object.entries(this._config).forEach(([key, value]) => {
            this._document.documentElement.style.setProperty(`--${key}`, value);
        });

        // Load fonts based on configuration
        this._loadFonts();

        // Set font paths based on appBasePath
        this._setFontPaths();

        // Set logo path based on appBasePath
        this._setLogoPath();

        // Set translations path based on appBasePath
        this._setTranslationsPath();
    }

    private _loadFonts() {
        const fontFamily = this._appConfig.ui['font-family'] || 'open-sans';
        let bodyFontFamily = '"Open Sans", Arial, sans-serif';
        
        if (fontFamily === 'titillium') {
            bodyFontFamily = '"Titillium Web", Arial, sans-serif';
        } else if (fontFamily === 'open-sans') {
            bodyFontFamily = '"Open Sans", Arial, sans-serif';
            // Import Google Fonts via CSS
            const link = this._document.createElement('link');
            link.rel = 'stylesheet';
            link.href = 'https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500;1,600;1,700&display=swap';
            this._document.head.appendChild(link);
        }
        
        // Set the body font family variable
        this._document.documentElement.style.setProperty('--body-font-family', bodyFontFamily);
    }

    private _setFontPaths() {
        const appBasePath = this._appConfig.frontend.appBasePath || '';
        const fontBasePath = appBasePath ? `${appBasePath}/typography/` : '/typography/';

        const fontVariables = [
            'typography-regular',
            'typography-bold',
            'typography-italic',
            'typography-bold-italic',
            'typography-light',
            'typography-light-italic',
            'typography-extra-light',
            'typography-extra-light-italic',
            'typography-semi-bold',
            'typography-semi-bold-italic'
        ];

        const fontFiles = [
            'TitilliumWeb-Regular.ttf',
            'TitilliumWeb-Bold.ttf',
            'TitilliumWeb-Italic.ttf',
            'TitilliumWeb-BoldItalic.ttf',
            'TitilliumWeb-Light.ttf',
            'TitilliumWeb-LightItalic.ttf',
            'TitilliumWeb-ExtraLight.ttf',
            'TitilliumWeb-ExtraLightItalic.ttf',
            'TitilliumWeb-SemiBold.ttf',
            'TitilliumWeb-SemiBoldItalic.ttf'
        ];

        fontVariables.forEach((variable, index) => {
            const fullPath = `url('${fontBasePath}${fontFiles[index]}')`;
            this._document.documentElement.style.setProperty(`--${variable}`, fullPath);
        });
    }

    private _setLogoPath() {
        const appBasePath = this._appConfig.frontend.appBasePath || '';
        const logoPath = this._appConfig.ui.logo || '/images/eng-logo.jpg';

        // Remove leading slash if present to avoid double slashes
        const cleanLogoPath = logoPath.startsWith('/') ? logoPath.substring(1) : logoPath;
        const fullLogoPath = appBasePath ? `${appBasePath}/${cleanLogoPath}` : `/${cleanLogoPath}`;

        this._document.documentElement.style.setProperty('--logo-path', `url('${fullLogoPath}')`);
    }

    private _setTranslationsPath() {
        const appBasePath = this._appConfig.frontend.appBasePath || '';
        const translationsPath = this._appConfig.i18n?.translationsPath || 'i18n';
        // Normalize paths to avoid double slashes
        const cleanBasePath = appBasePath.replace(/\/+$/, ''); // remove trailing slashes
        const cleanTranslationsPath = translationsPath.replace(/^\/+/, ''); // remove leading slashes
        const fullTranslationsPath = cleanBasePath
            ? `${cleanBasePath}/${cleanTranslationsPath}`
            : `/${cleanTranslationsPath}`;

        this._document.documentElement.style.setProperty('--translations-path', fullTranslationsPath);

        // Set global variable that can be used by the translation loader
        (window as Window & typeof globalThis & { __TRANSLATIONS_BASE_PATH__?: string }).__TRANSLATIONS_BASE_PATH__ =
            fullTranslationsPath;
    }
}
